import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { register } from '../store/slices/authSlice';
import { RootState, useAppDispatch } from '../store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import LandingNavbar from '@/components/LandingNavbar';
import IndexMark from '@/components/index/IndexMark';

/**
 * Register page component – same look and feel as Login / Nuclius Index pages.
 */
const Register: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { loading, error } = useSelector((state: RootState) => state.auth);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    terms?: string;
  }>({});

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!name.trim()) newErrors.name = 'Name is required';
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!agreeToTerms) newErrors.terms = 'You must agree to the terms and conditions';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const resultAction = await dispatch(register({ name, email, password }));
      if (register.fulfilled.match(resultAction)) {
        const { homePathForCurrentUser } = await import('@/hooks/useUserRoles');
        navigate(await homePathForCurrentUser(), { replace: true });
      }
    } catch (err) {
      console.error('Registration failed:', err);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-beige">
      <LandingNavbar />

      <main className="flex-1 flex items-center justify-center pt-24 pb-10 px-4">
        <div className="w-full max-w-md">
          <div className="bg-beige-dark rounded-sm p-8 border border-border-light shadow-sm">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="mb-4">
                <IndexMark theme="light" />
                <p className="text-[10px] font-mono uppercase tracking-wider text-text-dark/50 mt-2">
                  Forensic screenplay intelligence
                </p>
              </div>
              <h1 className="text-2xl font-sans font-bold text-text-dark tracking-tight">
                Create workspace account
              </h1>
              <p className="mt-2 text-sm text-text-dark opacity-70 font-sans">
                Register to ingest screenplays and run Nuclius Index audits
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium text-text-dark font-sans">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  autoComplete="name"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  className="w-full bg-white border-border-light text-text-dark"
                />
                {errors.name && (
                  <p className="text-xs text-red-600 font-sans">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-text-dark font-sans">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="w-full bg-white border-border-light text-text-dark"
                />
                {errors.email && (
                  <p className="text-xs text-red-600 font-sans">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-text-dark font-sans">
                  Password *
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="w-full bg-white border-border-light text-text-dark"
                />
                {errors.password && (
                  <p className="text-xs text-red-600 font-sans">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-text-dark font-sans">
                  Confirm Password *
                </label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  className="w-full bg-white border-border-light text-text-dark"
                />
                {errors.confirmPassword && (
                  <p className="text-xs text-red-600 font-sans">{errors.confirmPassword}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    disabled={loading}
                    className="mt-1 rounded border-border-light text-gold-button focus:ring-gold-button"
                  />
                  <span className="text-sm text-text-dark font-sans">
                    I agree to the terms and conditions
                  </span>
                </label>
                {errors.terms && (
                  <p className="text-xs text-red-600 font-sans">{errors.terms}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full py-2 mt-2 bg-gold-button text-white hover:bg-gold-button-dark font-sans"
                disabled={loading}
              >
                {loading ? (
                  <div className="h-5 w-5 border-2 border-t-transparent border-white rounded-full animate-spin mx-auto" />
                ) : (
                  'Sign Up'
                )}
              </Button>

              <div className="mt-4 text-right">
                <p className="text-sm text-text-dark opacity-70 font-sans">
                  Already have an account?{' '}
                  <RouterLink
                    to="/login"
                    className="text-gold-button hover:underline font-medium font-sans"
                  >
                    Sign in
                  </RouterLink>
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Register;
